/**
 * burnout-risk.ts
 *
 * Pure logic for computing per-resource burnout risk from weekly utilisation
 * data. No React, no DOM — only data in, data out.
 *
 * Risk classification
 * ───────────────────
 * CRITICAL  (red "↑")
 *   • Any single week > 110 %
 *   • Two or more consecutive weeks above 90 %
 *   • A 3+ week rising trend whose final week ends above 85 %
 *
 * HIGH      (amber "↑")
 *   • Any single week 100 – 110 %
 *   • Three or more weeks above 80 %
 *
 * NONE      — neither rule fires.
 */

import {
  startOfWeek,
  endOfWeek,
  addWeeks,
  eachDayOfInterval,
  getDay,
  getISOWeek,
} from 'date-fns';
import { Resource, Booking, WorkingHoursProfile } from '@/types';

export type BurnoutRiskLevel = 'critical' | 'high' | 'none';

export interface WeekUtilisation {
  weekStart: Date;
  weekEnd: Date;
  utilisationPct: number;
  bookedHours: number;
  availableHours: number;
  bookings: Booking[];
}

export interface BurnoutRisk {
  resourceId: string;
  level: BurnoutRiskLevel;
  /** Which weeks form the upward trend (used to mark heatmap cells) */
  trendWeekStarts: Date[];
  /** Human-readable reason for the risk flag */
  reason: string;
  /** Peak weekly utilisation observed in the window */
  peakUtilisation: number;
  weeklyData: WeekUtilisation[];
}

// ─── Working hours helper ────────────────────────────────────────────────────

const DAY_KEYS = [
  'sunday',
  'monday',
  'tuesday',
  'wednesday',
  'thursday',
  'friday',
  'saturday',
] as const;

function workingHoursForDay(
  resource: Resource,
  date: Date,
  globalProfile: WorkingHoursProfile
): number {
  const key = DAY_KEYS[getDay(date)];
  if (resource.customWorkingProfile && Object.keys(resource.customWorkingProfile).length > 0) {
    return (resource.customWorkingProfile as unknown as Record<string, number>)[key] ?? 0;
  }
  return (globalProfile as unknown as Record<string, number>)[key] ?? 0;
}

// ─── Per-week utilisation ────────────────────────────────────────────────────

function computeWeekUtilisation(
  resource: Resource,
  weekStart: Date,
  weekEnd: Date,
  bookings: Booking[],
  globalProfile: WorkingHoursProfile
): WeekUtilisation {
  // Available hours in this week for this resource
  let availableHours = 0;
  const days = eachDayOfInterval({ start: weekStart, end: weekEnd });
  days.forEach((d) => {
    availableHours += workingHoursForDay(resource, d, globalProfile);
  });

  // Bookings that overlap with this week (excluding cancelled)
  // Normalise endTime for bookings where endTime <= startTime (DB stores
  // single-day bookings with same start/end timestamp).
  const relevantBookings = bookings.filter((b) => {
    if (String(b.resourceId) !== String(resource.id)) return false;
    if (b.status === 'cancelled') return false;
    const bStart = new Date(b.startTime);
    let bEnd = new Date(b.endTime);

    // Normalise durationType to detect full-day / half-day variants
    const dtNorm = (b.durationType || '').toLowerCase();
    const isFullDay = dtNorm === 'full-day' || dtNorm === 'fullday' || dtNorm === 'full day' || dtNorm === 'full_day' || dtNorm === 'days' || dtNorm === 'weeks';
    const isHalfDay = dtNorm === 'half-day' || dtNorm === 'half-day-am' || dtNorm === 'half-day-pm' || dtNorm === 'halfday' || dtNorm === 'halfday-am' || dtNorm === 'halfday-pm';
    const isHourly = !isFullDay && !isHalfDay;

    // Day-based bookings with endTime <= startTime: advance end to next day
    if (!isHourly && bEnd.getTime() <= bStart.getTime()) {
      bEnd = new Date(bStart);
      bEnd.setHours(0, 0, 0, 0);
      bEnd.setDate(bEnd.getDate() + 1);
    }
    // Hourly bookings with endTime <= startTime: use durationHours or fallback
    if (isHourly && bEnd.getTime() <= bStart.getTime()) {
      if (b.durationHours && b.durationHours > 0) {
        bEnd = new Date(bStart.getTime() + b.durationHours * 60 * 60 * 1000);
      } else {
        bEnd = new Date(bStart);
        bEnd.setHours(0, 0, 0, 0);
        bEnd.setDate(bEnd.getDate() + 1);
      }
    }

    return bStart < weekEnd && bEnd > weekStart;
  });

  // Calculate booked hours within this week
  let bookedHours = 0;
  relevantBookings.forEach((b) => {
    const bStart = new Date(b.startTime);
    let bEnd = new Date(b.endTime);

    // Normalise durationType (mirrors the heatmap computation)
    const dtNorm = (b.durationType || '').toLowerCase();
    const isFullDay = dtNorm === 'full-day' || dtNorm === 'fullday' || dtNorm === 'full day' || dtNorm === 'full_day' || dtNorm === 'days' || dtNorm === 'weeks';
    const isHalfDay = dtNorm === 'half-day' || dtNorm === 'half-day-am' || dtNorm === 'half-day-pm' || dtNorm === 'halfday' || dtNorm === 'halfday-am' || dtNorm === 'halfday-pm';
    const isHourly = !isFullDay && !isHalfDay;

    // Normalise endTime for same-timestamp bookings
    if (!isHourly && bEnd.getTime() <= bStart.getTime()) {
      bEnd = new Date(bStart);
      bEnd.setHours(0, 0, 0, 0);
      bEnd.setDate(bEnd.getDate() + 1);
    }
    if (isHourly && bEnd.getTime() <= bStart.getTime()) {
      if (b.durationHours && b.durationHours > 0) {
        bEnd = new Date(bStart.getTime() + b.durationHours * 60 * 60 * 1000);
      } else {
        bEnd = new Date(bStart);
        bEnd.setHours(0, 0, 0, 0);
        bEnd.setDate(bEnd.getDate() + 1);
      }
    }

    const overlapStart = bStart < weekStart ? weekStart : bStart;
    const overlapEnd = bEnd > weekEnd ? weekEnd : bEnd;

    if (isFullDay) {
      const bStartDay = new Date(bStart);
      bStartDay.setHours(0, 0, 0, 0);
      const bEndDay = new Date(bEnd);
      bEndDay.setHours(0, 0, 0, 0);
      days.forEach((d) => {
        if (bStartDay <= d && bEndDay > d) {
          bookedHours += workingHoursForDay(resource, d, globalProfile);
        }
      });
    } else if (isHalfDay) {
      const bStartDay = new Date(bStart);
      bStartDay.setHours(0, 0, 0, 0);
      const bEndDay = new Date(bEnd);
      bEndDay.setHours(0, 0, 0, 0);
      days.forEach((d) => {
        if (bStartDay <= d && bEndDay > d) {
          bookedHours += workingHoursForDay(resource, d, globalProfile) / 2;
        }
      });
    } else {
      // hourly
      bookedHours +=
        (overlapEnd.getTime() - overlapStart.getTime()) / (1000 * 60 * 60);
    }
  });

  const utilisationPct =
    availableHours > 0 ? (bookedHours / availableHours) * 100 : 0;

  return {
    weekStart,
    weekEnd,
    utilisationPct,
    bookedHours,
    availableHours,
    bookings: relevantBookings,
  };
}

// ─── Risk detection ──────────────────────────────────────────────────────────

/**
 * Analyse a resource over the given look-ahead window (default 8 weeks).
 */
export function computeBurnoutRisk(
  resource: Resource,
  bookings: Booking[],
  globalProfile: WorkingHoursProfile,
  windowStart: Date = new Date(),
  weeksAhead = 8
): BurnoutRisk {
  // Build weekly data covering the window
  const weeks: WeekUtilisation[] = [];
  for (let i = 0; i < weeksAhead; i++) {
    const ws = startOfWeek(addWeeks(windowStart, i), { weekStartsOn: 1 });
    const we = endOfWeek(ws, { weekStartsOn: 1 });
    weeks.push(computeWeekUtilisation(resource, ws, we, bookings, globalProfile));
  }

  const utils = weeks.map((w) => w.utilisationPct);
  const peakUtilisation = Math.max(...utils, 0);

  // ── CRITICAL checks ──────────────────────────────────────────────────────

  // 1. Any week > 110 %
  const over110 = utils.findIndex((u) => u > 110);
  if (over110 !== -1) {
    return {
      resourceId: resource.id,
      level: 'critical',
      trendWeekStarts: [weeks[over110].weekStart],
      reason: `Week ${over110 + 1} at ${Math.round(utils[over110])}% (over 110%)`,
      peakUtilisation,
      weeklyData: weeks,
    };
  }

  // 2. Two or more consecutive weeks above 90 %
  for (let i = 0; i < utils.length - 1; i++) {
    if (utils[i] > 90 && utils[i + 1] > 90) {
      return {
        resourceId: resource.id,
        level: 'critical',
        trendWeekStarts: [weeks[i].weekStart, weeks[i + 1].weekStart],
        reason: `${Math.round(utils[i])}% then ${Math.round(utils[i + 1])}% — 2+ consecutive weeks above 90%`,
        peakUtilisation,
        weeklyData: weeks,
      };
    }
  }

  // 3. 3+ week rising trend ending above 85 %
  const trendWeeks = detectRisingTrend(utils, 3, 85);
  if (trendWeeks.length > 0) {
    return {
      resourceId: resource.id,
      level: 'critical',
      trendWeekStarts: trendWeeks.map((idx) => weeks[idx].weekStart),
      reason: `${trendWeeks.length}-week upward trend ending at ${Math.round(utils[trendWeeks[trendWeeks.length - 1]])}%`,
      peakUtilisation,
      weeklyData: weeks,
    };
  }

  // ── HIGH checks ──────────────────────────────────────────────────────────

  // 4. Any single week 100–110 %
  const over100 = utils.findIndex((u) => u >= 100 && u <= 110);
  if (over100 !== -1) {
    return {
      resourceId: resource.id,
      level: 'high',
      trendWeekStarts: [weeks[over100].weekStart],
      reason: `Week ${over100 + 1} at ${Math.round(utils[over100])}% (100–110%)`,
      peakUtilisation,
      weeklyData: weeks,
    };
  }

  // 5. Three or more weeks above 80 %
  const weeksAbove80 = utils.reduce<number[]>(
    (acc, u, i) => (u > 80 ? [...acc, i] : acc),
    []
  );
  if (weeksAbove80.length >= 3) {
    return {
      resourceId: resource.id,
      level: 'high',
      trendWeekStarts: weeksAbove80.map((i) => weeks[i].weekStart),
      reason: `${weeksAbove80.length} weeks above 80%`,
      peakUtilisation,
      weeklyData: weeks,
    };
  }

  return {
    resourceId: resource.id,
    level: 'none',
    trendWeekStarts: [],
    reason: '',
    peakUtilisation,
    weeklyData: weeks,
  };
}

/**
 * Returns the indices of the longest run of consecutive strictly-increasing
 * values in `utils` that is at least `minLength` long and whose last value
 * is at or above `minEndPct`. Returns [] if no such run exists.
 */
function detectRisingTrend(
  utils: number[],
  minLength: number,
  minEndPct: number
): number[] {
  let bestRun: number[] = [];
  let currentRun: number[] = [0];

  for (let i = 1; i < utils.length; i++) {
    if (utils[i] > utils[i - 1]) {
      currentRun.push(i);
    } else {
      if (
        currentRun.length >= minLength &&
        utils[currentRun[currentRun.length - 1]] >= minEndPct
      ) {
        if (currentRun.length > bestRun.length) bestRun = [...currentRun];
      }
      currentRun = [i];
    }
  }
  // Final flush
  if (
    currentRun.length >= minLength &&
    utils[currentRun[currentRun.length - 1]] >= minEndPct
  ) {
    if (currentRun.length > bestRun.length) bestRun = [...currentRun];
  }

  return bestRun;
}

// ─── Suggestion generation ───────────────────────────────────────────────────

export interface SharedSkill {
  name: string;
  /** Candidate's proficiency rating 1–5 */
  rating: number;
  /** At-risk resource's proficiency rating 1–5 (shows parity) */
  atRiskRating: number;
}

export interface RebalancingSuggestion {
  atRiskResourceId: string;
  atRiskResourceName: string;
  level: BurnoutRiskLevel;
  peakUtilisation: number;
  trendSummary: string;
  /** The project generating the most load on the at-risk resource */
  projectName: string;
  /** Which weeks are most affected */
  affectedWeekStarts: Date[];
  /** Candidate resource to redistribute work to */
  candidateId: string;
  candidateName: string;
  candidateRole: string;
  candidateAvgUtilisation: number;
  /** Whether role matches */
  sameRole: boolean;
  /** Shared skills with full proficiency data */
  sharedSkills: SharedSkill[];
  /** 0–100 composite score: skill overlap (70%) + capacity (30%) */
  candidateScore: number;
  /** Skills the at-risk resource has that the candidate is missing */
  missingSkills: string[];
  /** Total required skills count (for coverage % display) */
  totalAtRiskSkills: number;
}

/**
 * Generate simple redistribution suggestions for all at-risk resources.
 * For each at-risk resource we look for the best available candidate:
 *   1. Same role preferred
 *   2. Lowest average utilisation in the affected weeks
 *   3. Must not themselves be at-risk
 */
export function generateSuggestions(
  atRiskItems: BurnoutRisk[],
  allResources: Resource[],
  allBookings: Booking[],
  globalProfile: WorkingHoursProfile
): RebalancingSuggestion[] {
  const suggestions: RebalancingSuggestion[] = [];

  // Pre-compute average utilisation over next 4 weeks for every resource
  const now = new Date();
  const avgUtilMap = new Map<string, number>();
  allResources.forEach((r) => {
    if (r.id === 'unallocated') return;
    let total = 0;
    for (let i = 0; i < 4; i++) {
      const ws = startOfWeek(addWeeks(now, i), { weekStartsOn: 1 });
      const we = endOfWeek(ws, { weekStartsOn: 1 });
      const wu = computeWeekUtilisation(r, ws, we, allBookings, globalProfile);
      total += wu.utilisationPct;
    }
    avgUtilMap.set(r.id, total / 4);
  });

  const atRiskIds = new Set(atRiskItems.map((r) => r.resourceId));

  atRiskItems.forEach((risk) => {
    const atRiskResource = allResources.find(
      (r) => String(r.id) === String(risk.resourceId)
    );
    if (!atRiskResource) return;

    // Find the project with the most bookings on this resource in the trend window
    const bookingCounts: Record<string, { name: string; count: number }> = {};
    risk.weeklyData.forEach((wd) => {
      wd.bookings.forEach((b) => {
        const pid = b.projectId ?? 'unknown';
        if (!bookingCounts[pid]) {
          bookingCounts[pid] = { name: b.projectName ?? 'Unknown Project', count: 0 };
        }
        bookingCounts[pid].count++;
      });
    });
    const topProject =
      Object.values(bookingCounts).sort((a, b) => b.count - a.count)[0] ??
      { name: 'Unknown Project', count: 0 };

    // Derive the concrete date range the at-risk bookings span so we can
    // check candidate availability on those exact days (not just average util).
    const affectedBookings = allBookings.filter(
      (b) =>
        String(b.resourceId) === String(atRiskResource.id) &&
        (b.projectName ?? '') === topProject.name
    );
    const affectedDates: Array<{ start: Date; end: Date }> = affectedBookings.map((b) => ({
      start: new Date(b.startTime),
      end: new Date(b.endTime),
    }));

    /** Returns true if the resource has any booking that overlaps the affected dates */
    const hasConflictOnAffectedDates = (resourceId: string): boolean => {
      if (affectedDates.length === 0) return false;
      return allBookings.some((b) => {
        if (String(b.resourceId) !== String(resourceId)) return false;
        const bStart = new Date(b.startTime);
        const bEnd = new Date(b.endTime);
        return affectedDates.some(({ start, end }) => bStart < end && bEnd > start);
      });
    };

    // ── Skill map for the at-risk resource ──────────────────────────────────
    // name.toLowerCase() → { name, rating } for O(1) lookup
    const atRiskSkillMap = new Map<string, { name: string; rating: number }>(
      (atRiskResource.skills ?? []).map((s) => [s.name.toLowerCase(), s])
    );
    const totalAtRiskSkills = atRiskSkillMap.size;

    // ── Composite scorer ──────────────────────────────────────────────────────
    // Score = (skillSignal × 0.70) + (capacitySignal × 0.30)
    //
    // skillSignal  — proficiency-weighted skill overlap (0–100).
    //   For each skill the at-risk resource has, check if the candidate
    //   also has it. Contribution per matched skill:
    //     (candidateRating / 5) × (1 / totalAtRiskSkills) × 100
    //   This weights high-proficiency candidates more and penalises
    //   candidates missing skills entirely.
    //
    // capacitySignal — inverse of avg utilisation (0–100).
    //   100% free → 100, 50% used → 50, 100% used → 0.
    //   Clamped to [0, 100].
    //
    // sameRole bonus: +10 pts added after composite, capped at 100.
    const scoreCandidate = (r: Resource): number => {
      let weightedSkill = 0;
      if (totalAtRiskSkills > 0) {
        (r.skills ?? []).forEach((cs) => {
          const atRiskSkill = atRiskSkillMap.get(cs.name.toLowerCase());
          if (atRiskSkill) {
            // Candidate rating / 5 normalised per skill share
            weightedSkill += (cs.rating / 5) * (1 / totalAtRiskSkills) * 100;
          }
        });
      } else {
        // No skills recorded on at-risk resource — skill signal neutral (50)
        weightedSkill = 50;
      }

      const avgUtil = avgUtilMap.get(r.id) ?? 100;
      const capacitySignal = Math.max(0, 100 - avgUtil);

      const roleBonus = r.role === atRiskResource.role ? 10 : 0;
      return Math.min(100, weightedSkill * 0.70 + capacitySignal * 0.30 + roleBonus);
    };

    // Candidate selection: not at-risk, not unallocated, no conflict on
    // affected dates, scored by the composite function above.
    const candidates = allResources.filter(
      (r) =>
        r.id !== 'unallocated' &&
        !atRiskIds.has(r.id) &&
        r.id !== atRiskResource.id &&
        !hasConflictOnAffectedDates(r.id)
    );

    // Prefer same-role pool; fall back to all candidates if empty
    const sameRoleCandidates = candidates.filter((r) => r.role === atRiskResource.role);
    const pool = sameRoleCandidates.length > 0 ? sameRoleCandidates : candidates;

    if (pool.length === 0) return;

    // Pick the highest-scoring candidate
    const scoredPool = pool.map((r) => ({ resource: r, score: scoreCandidate(r) }));
    scoredPool.sort((a, b) => b.score - a.score);
    const { resource: best, score: candidateScore } = scoredPool[0];

    // ── Build shared / missing skill detail ──────────────────────────────────
    const candidateSkillMap = new Map<string, number>(
      (best.skills ?? []).map((s) => [s.name.toLowerCase(), s.rating])
    );

    const sharedSkills: SharedSkill[] = [];
    const missingSkills: string[] = [];

    atRiskSkillMap.forEach(({ name, rating: atRiskRating }, key) => {
      const candidateRating = candidateSkillMap.get(key);
      if (candidateRating !== undefined) {
        sharedSkills.push({ name, rating: candidateRating, atRiskRating });
      } else {
        missingSkills.push(name);
      }
    });

    // Sort shared skills: highest candidate proficiency first
    sharedSkills.sort((a, b) => b.rating - a.rating);

    suggestions.push({
      atRiskResourceId: atRiskResource.id,
      atRiskResourceName: atRiskResource.name,
      level: risk.level,
      peakUtilisation: Math.round(risk.peakUtilisation),
      trendSummary: risk.reason,
      projectName: topProject.name,
      affectedWeekStarts: risk.trendWeekStarts,
      candidateId: best.id,
      candidateName: best.name,
      candidateRole: best.role,
      candidateAvgUtilisation: Math.round(avgUtilMap.get(best.id) ?? 0),
      sameRole: best.role === atRiskResource.role,
      sharedSkills,
      candidateScore: Math.round(candidateScore),
      missingSkills,
      totalAtRiskSkills,
    });
  });

  return suggestions;
}

// ─── Coming-free detection ────────────────────────────────────────────────────

export interface ComingFreeResource {
  resourceId: string;
  resourceName: string;
  resourceRole: string;
  /** ISO week number of the first "free" week (e.g. 20) */
  freeFromWeek: number;
  /** Utilisation in the week BEFORE the drop (0–100) */
  utilisationBefore: number;
  /** Utilisation in the first free week (0–100) */
  utilisationAfter: number;
  /** Number of consecutive free weeks meeting the threshold */
  sustainedWeeks: number;
  /** Estimated available capacity as a percentage (100 – utilisationAfter) */
  availableCapacityPct: number;
  /** Name of the ending project/booking, if identifiable */
  endingProjectName: string | null;
  /** The week start Date for sorting / display */
  freeFromWeekStart: Date;
}

export interface UpcomingAvailabilitySuggestion {
  comingFree: ComingFreeResource;
  /** Up to 2 matched resources/projects that need capacity */
  matches: UpcomingMatch[];
}

export interface UpcomingMatch {
  resourceId: string;
  resourceName: string;
  resourceRole: string;
  /** Why this is a good match */
  matchReason: string;
  /** Shared skill names */
  sharedSkills: string[];
  /** Current avg utilisation of the overburdened / needy resource */
  utilisation: number;
  /** 'at-risk' = overburdened resource, 'unallocated' = unallocated booking needing a resource */
  matchType: 'at-risk' | 'unallocated';
}

/**
 * Detect resources whose utilisation drops by ≥40 pp over a sustained period
 * (2+ consecutive weeks below the threshold) within the next 8 weeks.
 */
export function detectComingFreeResources(
  resources: Resource[],
  bookings: Booking[],
  globalProfile: WorkingHoursProfile,
  windowStart: Date = new Date(),
  weeksAhead = 8
): ComingFreeResource[] {
  const results: ComingFreeResource[] = [];

  resources
    .filter((r) => r.id !== 'unallocated')
    .forEach((resource) => {
      // Build weekly utilisation series for the window
      const weeks: WeekUtilisation[] = [];
      for (let i = 0; i < weeksAhead; i++) {
        const ws = startOfWeek(addWeeks(windowStart, i), { weekStartsOn: 1 });
        const we = endOfWeek(ws, { weekStartsOn: 1 });
        weeks.push(computeWeekUtilisation(resource, ws, we, bookings, globalProfile));
      }

      const utils = weeks.map((w) => w.utilisationPct);

      // Find first week where utilisation drops ≥40 pp vs. the previous week
      // and the drop is sustained across ≥2 consecutive weeks.
      for (let i = 1; i < utils.length; i++) {
        const drop = utils[i - 1] - utils[i];
        if (drop < 40) continue;

        // Count how many consecutive weeks maintain the lower level
        let sustainedCount = 1;
        for (let j = i + 1; j < utils.length; j++) {
          if (utils[j - 1] - utils[j - 1] <= 5 && utils[j] < utils[i - 1] - 30) {
            sustainedCount++;
          } else if (utils[j] < utils[i - 1] - 30) {
            sustainedCount++;
          } else {
            break;
          }
        }

        if (sustainedCount < 2) continue;

        // Identify the project that ends, causing the drop
        // It is the project active in week i-1 but NOT in week i
        const prevBookingProjectNames = new Set(
          weeks[i - 1].bookings.map((b) => b.projectName ?? '')
        );
        const nextBookingProjectNames = new Set(
          weeks[i].bookings.map((b) => b.projectName ?? '')
        );
        const endingProjects = [...prevBookingProjectNames].filter(
          (n) => n && !nextBookingProjectNames.has(n)
        );
        const endingProjectName =
          endingProjects.length > 0 ? endingProjects[0] : null;

        const availableCapacityPct = Math.round(
          Math.max(0, 100 - utils[i])
        );

        results.push({
          resourceId: resource.id,
          resourceName: resource.name,
          resourceRole: resource.role,
          freeFromWeek: getISOWeek(weeks[i].weekStart),
          utilisationBefore: Math.round(utils[i - 1]),
          utilisationAfter: Math.round(utils[i]),
          sustainedWeeks: sustainedCount,
          availableCapacityPct,
          endingProjectName,
          freeFromWeekStart: weeks[i].weekStart,
        });

        // Only report the first qualifying drop per resource
        break;
      }
    });

  // Sort chronologically
  results.sort(
    (a, b) => a.freeFromWeekStart.getTime() - b.freeFromWeekStart.getTime()
  );

  return results;
}

/**
 * For each coming-free resource, find up to 2 good matches:
 *  - At-risk (overburdened) resources with overlapping skills
 *  - Unallocated bookings whose requiredSkills / requiredRole overlap
 */
export function generateUpcomingAvailabilitySuggestions(
  comingFreeList: ComingFreeResource[],
  atRiskResources: BurnoutRisk[],
  allResources: Resource[],
  allBookings: Booking[],
  globalProfile: WorkingHoursProfile
): UpcomingAvailabilitySuggestion[] {
  const now = new Date();

  // Pre-compute average utilisation for at-risk resources
  const avgUtilMap = new Map<string, number>();
  allResources.forEach((r) => {
    if (r.id === 'unallocated') return;
    let total = 0;
    for (let i = 0; i < 4; i++) {
      const ws = startOfWeek(addWeeks(now, i), { weekStartsOn: 1 });
      const we = endOfWeek(ws, { weekStartsOn: 1 });
      total += computeWeekUtilisation(r, ws, we, allBookings, globalProfile).utilisationPct;
    }
    avgUtilMap.set(r.id, total / 4);
  });

  const atRiskIds = new Set(atRiskResources.map((r) => r.resourceId));

  return comingFreeList.map((cf) => {
    const cfResource = allResources.find((r) => String(r.id) === String(cf.resourceId));
    const cfSkillNames = new Set(
      (cfResource?.skills ?? []).map((s) => s.name.toLowerCase())
    );

    const matches: UpcomingMatch[] = [];

    // ── Match 1: overburdened at-risk resources with skill overlap ──────────
    atRiskIds.forEach((id) => {
      if (matches.length >= 2) return;
      if (String(id) === String(cf.resourceId)) return;
      const r = allResources.find((res) => String(res.id) === String(id));
      if (!r) return;
      const shared = (r.skills ?? [])
        .filter((s) => cfSkillNames.has(s.name.toLowerCase()))
        .map((s) => s.name)
        .slice(0, 2);
      const util = Math.round(avgUtilMap.get(r.id) ?? 0);
      matches.push({
        resourceId: r.id,
        resourceName: r.name,
        resourceRole: r.role,
        matchReason:
          shared.length > 0
            ? `Overburdened · shared: ${shared.join(', ')}`
            : `Overburdened · ${r.role}`,
        sharedSkills: shared,
        utilisation: util,
        matchType: 'at-risk',
      });
    });

    // ── Match 2: unallocated bookings whose role / skills match ─────────────
    if (matches.length < 2) {
      const unallocatedBookings = allBookings.filter((b) => {
        if (b.resourceId !== 'unallocated' && b.resourceId !== null) return false;
        if (b.status === 'cancelled') return false;
        // Must overlap with the coming-free window
        const bEnd = new Date(b.endTime);
        return bEnd >= cf.freeFromWeekStart;
      });

      for (const ub of unallocatedBookings) {
        if (matches.length >= 2) break;
        const reqRole = ub.requiredRole ?? '';
        const reqSkills = new Set((ub.requiredSkills ?? []).map((s) => s.toLowerCase()));
        const shared = [...cfSkillNames]
          .filter((s) => reqSkills.has(s))
          .map((s) =>
            (cfResource?.skills ?? []).find((sk) => sk.name.toLowerCase() === s)?.name ?? s
          )
          .slice(0, 2);
        const hasOverlap = shared.length > 0 || (reqRole !== '' && reqRole === cfResource?.role);
        if (!hasOverlap) continue;
        matches.push({
          resourceId: 'unallocated',
          resourceName: ub.projectName,
          resourceRole: reqRole || 'Any role',
          matchReason:
            shared.length > 0
              ? `Unallocated booking · needs: ${shared.join(', ')}`
              : `Unallocated booking · ${reqRole}`,
          sharedSkills: shared,
          utilisation: 0,
          matchType: 'unallocated',
        });
      }
    }

    return { comingFree: cf, matches };
  });
}
